/* cf-fresh-start.js - Click Fencing, 2026-09-02
 *
 * Pete: "now i have gone back into starting a new glass fence, its awkwardly pulls me through
 *        the sections of the last fence i built. perhaps if the customer goes in through to
 *        start a new fence it starts new with the options - reload last fence - and - Start a
 *        new design -"
 *
 * ---------------------------------------------------------------------------------------
 * WHAT HAPPENS TODAY. cf-calc-memory.js snapshots the working fence into
 * sessionStorage under cf-calc:<path> and restores it silently on the next visit. The
 * snapshot includes currentTab, so somebody clicking "Frameless Glass" from the menu to
 * start a fresh fence is dropped straight back onto Review & Checkout of the fence they
 * finished earlier, mid-flow, with no indication of why. Pete's screenshot is exactly
 * that: the glass calculator open on step 3 of an 8690mm raked fence he had already done.
 *
 * The memory itself is worth keeping - losing a half-built fence to a stray refresh is
 * worse - so this does not delete it. It just stops it being AUTOMATIC and makes it the
 * customer's choice, which is what Pete asked for.
 *
 * HOW, WITHOUT EDITING cf-calc-memory.js. The saved state is MOVED ASIDE to a holding key
 * before that file ever looks for it, so it finds nothing and boots the calculator clean.
 * The two buttons then either put it back (and reload, so the normal restore path runs
 * exactly as it always has) or drop it. Nothing about how the restore works is changed -
 * only whether it happens.
 *
 * ORDER IS LOAD-BEARING. This must run before cf-calc-memory.js reads the key, so it is
 * registered from cf-contrast-fixes.liquid, which renders ahead of it, and it does its
 * move at TOP LEVEL rather than waiting for DOMContentLoaded.
 *
 * WHAT IT NEVER TOUCHES. Saved plans - flattop_quotes, blade_quotes, perfmesh_quotes,
 * ullc_quotes and their counters - are the customer's library and are not working state.
 * "Start a new design" clears the fence being built and nothing else, exactly like the
 * existing Start again button.
 *
 * WHEN IT STAYS OUT OF THE WAY. Coming in from "Edit sections" on quote-review is a
 * deliberate request to rebuild a specific fence, so the handoff is detected and the
 * chooser is suppressed - being asked "new or old?" immediately after asking to edit an
 * old one would be nonsense.
 */
(function () {
  'use strict';

  if (window.__cfFreshStart) return;
  window.__cfFreshStart = true;

  var BASES = {
    '/pages/flat-top-fence-calculator': 'flattop',
    '/pages/blade-fence-calculator': 'blade',
    '/pages/perfmesh-fence': 'perfmesh',
    '/pages/glass-fencing': 'ullc'
  };

  var path = String(location.pathname || '');
  var base = BASES[path];
  if (!base) return;

  var MEM_KEY = 'cf-calc:' + path;
  var HOLD_KEY = 'cf-fresh-hold:' + path;
  var EDIT_HANDOFF = 'cf-quote-edit';
  var LOG = '[cf-fresh-start]';

  function ss(fn, dflt) { try { return fn(); } catch (e) { return dflt; } }

  /* ------------------------------------------------- move the memory aside */

  /* A snapshot is only worth offering if it describes a fence somebody actually built.
     cf-calc-memory writes on any form interaction, so an empty first visit can leave a
     snapshot carrying nothing but defaults - offering to "reload" that would be noise. */
  function worthOffering(raw) {
    if (!raw || raw.length < 40) return false;
    return /"length"\s*:\s*"?\d{3,}/.test(raw) || /sections/.test(raw);
  }

  var held = null;

  (function moveAside() {
    /* 2026-09-26: arriving from "Start again" (?cf-reset=1) means a blank sheet. Drop both
       copies and stop — no restore, no "Reload last fence". */
    if (/[?&]cf-reset=1\b/.test(location.search)) {
      ss(function () { sessionStorage.removeItem(MEM_KEY); sessionStorage.removeItem(HOLD_KEY); });
      return;
    }

    /* "Edit sections" is an explicit request to rebuild one particular fence. */
    if (ss(function () { return sessionStorage.getItem(EDIT_HANDOFF); }, null)) return;

    /* 2026-09-26 — MIXED FENCE JOBS. cf-job-ui.js leaves 'cf-job-arrive' when it moves the
       customer between fences of one job. 'resume' = they are going BACK to a fence they
       already designed in this job: leave the memory where it is so it restores exactly as
       they left it (colour, fixing, gates — Pete: going back to glass lost the colour).
       'fresh' = first visit to this fence in the job: drop any older fence silently, because
       "You have a fence from last time" in the middle of a job reads as broken. */
    /* 2026-09-26 (break test F3, Pete "fix it"): a REFRESH or the browser BACK button is the
       customer returning to the fence they were building, not starting a new one — every
       entry used to be thrown away. Leave the memory in place so cf-calc-memory.js restores it
       straight away, exactly as it does inside a mixed job. */
    var navType = ss(function () {
      var e = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return e ? e.type : (performance.navigation && performance.navigation.type === 1 ? 'reload' : '');
    }, '');
    if (navType === 'reload' || navType === 'back_forward') {
      var parked = ss(function () { return sessionStorage.getItem(HOLD_KEY); }, null);
      if (parked && !ss(function () { return sessionStorage.getItem(MEM_KEY); }, null)) {
        ss(function () { sessionStorage.setItem(MEM_KEY, parked); sessionStorage.removeItem(HOLD_KEY); });
      }
      return;
    }

    /* 2026-09-26 (break test F5): a fence that belongs to the customer's mixed-fence job is
       THEIR fence, however they come back to it (menu, bookmark, typed address). Opening it blank
       made them rebuild it — and pressing "Continue to …" from the blank page filed a $0 fence. */
    var CALC_OF = { '/pages/glass-fencing': 'Frameless', '/pages/perfmesh-fence': 'PerfMesh',
                    '/pages/flat-top-fence-calculator': 'FlatTop', '/pages/blade-fence-calculator': 'Blade' };
    var inJob = ss(function () {
      var j = JSON.parse(localStorage.getItem('cf_job') || 'null');
      var items = j && j.plan && j.plan.items;
      if (!(items && items.some(function (it) { return it.calcType === CALC_OF[path]; }))) return false;
      /* only while the mixed job is ACTIVE in this tab (26 Sep: an old job must not take over) */
      if (sessionStorage.getItem('cf_job_active') !== '1') return false;
      /* only a fence built INSIDE this job, never an older one left in the tab */
      var raw = sessionStorage.getItem(MEM_KEY) || sessionStorage.getItem(HOLD_KEY);
      if (!raw) return false;
      var snap = JSON.parse(raw);
      return !!(snap && snap.ts && snap.ts >= (Date.parse(j.createdAt) || 0));
    }, false);
    if (inJob) {
      var parked2 = ss(function () { return sessionStorage.getItem(HOLD_KEY); }, null);
      if (parked2 && !ss(function () { return sessionStorage.getItem(MEM_KEY); }, null)) {
        ss(function () { sessionStorage.setItem(MEM_KEY, parked2); sessionStorage.removeItem(HOLD_KEY); });
      }
      /* a first-time arrival from the job (mode 'fresh') still starts clean, below */
      var arr0 = ss(function () { return JSON.parse(sessionStorage.getItem('cf-job-arrive') || 'null'); }, null);
      if (!(arr0 && arr0.path === path && arr0.mode === 'fresh' && (Date.now() - Number(arr0.at)) < 120000)) return;
    }

    var arrive = ss(function () { return JSON.parse(sessionStorage.getItem('cf-job-arrive') || 'null'); }, null);
    if (arrive && arrive.path === path && arrive.at && (Date.now() - Number(arrive.at)) < 120000) {
      if (arrive.mode === 'resume') return;
      ss(function () { sessionStorage.removeItem(MEM_KEY); sessionStorage.removeItem(HOLD_KEY); });
      return;
    }

    var raw = ss(function () { return sessionStorage.getItem(MEM_KEY); }, null);
    if (!raw) {
      /* A holding key left over from a reload we caused ourselves. */
      held = ss(function () { return sessionStorage.getItem(HOLD_KEY); }, null);
      return;
    }
    if (!worthOffering(raw)) return;

    held = raw;
    ss(function () {
      sessionStorage.setItem(HOLD_KEY, raw);
      sessionStorage.removeItem(MEM_KEY);
    });
    try { console.log(LOG, 'held the previous fence; calculator starts clean'); } catch (e) {}
  })();

  if (!held) return;

  /* ------------------------------------------------- the two choices */

  function reloadLast() {
    ss(function () {
      var raw = sessionStorage.getItem(HOLD_KEY);
      if (raw) sessionStorage.setItem(MEM_KEY, raw);
      sessionStorage.removeItem(HOLD_KEY);
    });
    /* Reload rather than restore by hand: cf-calc-memory's own restore is the tested path
       and it runs on load, so putting the key back and reloading gives exactly the
       behaviour that existed before this file. */
    location.reload();
  }

  /* The same set of keys the existing Start again button clears - working state only.
     *_quotes and *_quote_counter are the customer's saved plans and are NEVER touched. */
  function startNew() {
    ss(function () {
      sessionStorage.removeItem(HOLD_KEY);
      sessionStorage.removeItem(MEM_KEY);
      sessionStorage.removeItem(base + '_session');
      sessionStorage.removeItem(base + '_quote');
      localStorage.removeItem(base + '_active_quote');
    });
    dismiss();
  }

  /* ------------------------------------------------- the panel */

  /* WHERE THE CHOICE GOES. Pete, 2026-09-02: "the start a new design option and reload should
     be here. the start again is already there. this is for all calculator" - pointing at the
     existing memory bar, the strip reading "Your fence is saved while this browser is open."
     with its Start again button.

     He is right, and it is the same bar on all four calculators: cf-calc-memory.js renders
     #cf-calc-memory-bar containing #cf-start-again. So the choice belongs in that strip rather
     than in a panel of its own above the heading - it is the same subject, and one place to
     look beats two.

     While the choice is pending the page is ALREADY blank (the held fence was moved aside), so
     "Start again" has nothing to start again from and would read as a third near-identical
     button. It is hidden for that moment only and put straight back as soon as the customer
     picks, which is why the original is hidden rather than replaced. */

  var WRAP_ID = 'cf-fresh-start';
  var BAR_ID = 'cf-calc-memory-bar';
  var AGAIN_ID = 'cf-start-again';

  function restoreStartAgain() {
    try {
      var a = document.getElementById(AGAIN_ID);
      if (a && a.getAttribute('data-cf-fs-hid')) {
        a.style.removeProperty('display');
        a.removeAttribute('data-cf-fs-hid');
      }
    } catch (e) {}
  }

  function dismiss() {
    var el = document.getElementById(WRAP_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
    restoreStartAgain();
  }

  function css() {
    if (document.getElementById('cf-fresh-start-css')) return;
    var s = document.createElement('style');
    s.id = 'cf-fresh-start-css';
    s.textContent =
      '#' + WRAP_ID + '{display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap;' +
        'margin-left:8px;vertical-align:middle;}' +
      '#' + WRAP_ID + ' .cf-fs__lead{font-size:13px;color:#1e3a5f;font-weight:600;}' +
      '#' + WRAP_ID + ' button{font-size:13.5px;font-weight:600;padding:7px 14px;' +
        'border-radius:8px;cursor:pointer;border:1px solid #1d4ed8;line-height:1.2;}' +
      '#' + WRAP_ID + ' .cf-fs__old{background:#1d4ed8;color:#fff;}' +
      '#' + WRAP_ID + ' .cf-fs__old:hover{background:#1743b3;}' +
      '#' + WRAP_ID + ' .cf-fs__new{background:#fff;color:#1d4ed8;}' +
      '#' + WRAP_ID + ' .cf-fs__new:hover{background:#eff6ff;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function build() {
    css();
    var box = document.createElement('span');
    box.id = WRAP_ID;

    var lead = document.createElement('span');
    lead.className = 'cf-fs__lead';
    lead.textContent = 'You have a fence from last time —';

    var bOld = document.createElement('button');
    bOld.type = 'button';
    bOld.className = 'cf-fs__old';
    bOld.textContent = 'Reload last fence';
    bOld.addEventListener('click', function (e) { e.preventDefault(); reloadLast(); });

    var bNew = document.createElement('button');
    bNew.type = 'button';
    bNew.className = 'cf-fs__new';
    bNew.textContent = 'Start a new design';
    bNew.addEventListener('click', function (e) { e.preventDefault(); startNew(); });

    box.appendChild(lead);
    box.appendChild(bOld);
    box.appendChild(bNew);
    return box;
  }

  function place() {
    if (document.getElementById(WRAP_ID)) return;
    var bar = document.getElementById(BAR_ID);
    if (!bar) return;                       /* the bar has not rendered yet - try again */

    /* 2026-09-26 (break test F3): the bar is display:none until cf-calc-memory saves, and the
       memory was just moved aside, so this choice was being placed inside a hidden bar and
       never seen (measured 0x0 on all four). Show the bar. */
    bar.style.display = 'flex';
    var again = document.getElementById(AGAIN_ID);
    if (again && !again.getAttribute('data-cf-fs-hid')) {
      again.setAttribute('data-cf-fs-hid', '1');
      again.style.setProperty('display', 'none', 'important');
    }
    bar.appendChild(build());
  }

  function start() {
    place();
    /* The bar renders with the calculator shell, so poll for a few seconds - but stop rather
       than observing forever, because this choice is a one-off per visit. */
    var n = 0;
    (function poll() {
      if (n++ > 40 || document.getElementById(WRAP_ID)) return;
      place();
      setTimeout(poll, 300);
    })();
    try { console.log(LOG, 'offering reload-or-new in the memory bar'); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
